import db from "../db/database";
import {
  sendToBusinessCentral,
  fetchFromBusinessCentral,
  motivoDeRechazoBC,
  avisoTrasElOk,
  registrarEnBusinessCentral,
} from "../utils/businessCentralUtils";
import {
  avisoDelCobroQueNoCuadra,
  avisoDePagosQueNoCuadran,
  cobroQueNoCuadraEnBC,
  esRechazoPorPagoIncompleto,
  pagosQueNoCuadranEnBC,
} from "./cobroAntesDeRegistrar";
import { avisoDeLineasQueNoCuadran, lineasQueNoCuadranEnBC } from "./lineasAntesDeCobrar";
import { anotarAviso } from "./syncLog";
import { avisoDelFallo, limpiarMotivoTecnico, motivoParaElCajero } from "../utils/avisoDelFallo";
import { campoDeLaCaja, comportamientoDelPais, entidadDelPais } from "../config/integracionPorPais";
import { quitarLosCamposLocales } from "../utils/camposLocalesDeLaFactura";
import { calculateBCUnitPrice, calcularDescuentoDeFormaDePago } from "../utils/invoiceTotals";
import {
  addMoney,
  resolveMoneyDecimals,
  roundMoney,
  subtractMoney,
  sumMoney,
} from "../utils/money";
import { roundQuantity } from "../utils/formatNumbers";
import { isEmpty } from "../utils/common";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getClientByNoOrId } from "./clientService";
import { syncClients } from "./clientSyncService";
import { getInvoicePaymentLines } from "./invoicePaymentService";
import { getInvoiceLines } from "./invoiceService";
import { tipoSunatDeLaReferencia } from "./referenciaSunat";
import { getClientById } from "./clientService";
import { COUNTRY_CODES } from "../constants/countries";
import { elDocumento, TIPOS_DE_COMPROBANTE } from "../components/Invoice/tipoDeComprobante";
import {
  loQueRechazaria,
  revisarParaHacienda,
} from "../components/Invoice/extensions/costarica/validacionDeHacienda";
import { faltaElCorreoDelCliente } from "../components/Invoice/extensions/costarica/correoDelCliente";
import { revisarIvaDelDocumento } from "../components/Invoice/extensions/costarica/ivaDelDocumento";
import { clavesDeUbicacionParaBC } from "../components/Invoice/extensions/costarica/ubicacionDelReceptor";
import { revisarParaLaDGI } from "../components/Invoice/extensions/panama/validacionDeLaDGI";
import {
  avisoDelAbonoQueExcede,
  avisoDelClienteBloqueado,
  esRechazoDefinitivo,
  esRechazoPorAbonoQueExcede,
  esRechazoPorClienteBloqueado,
} from "./rechazoDefinitivo";
import {
  mapUiToSystemType,
  matchesDocumentType,
  mapDocumentTypeForBC,
} from "../constants/documentType";
import { findInvoiceInBCByIdTpv, reconcileInvoiceWithBC } from "./invoiceRecoveryService";
import {
  avisoDeLaCuentaDePagoSinConfigurar,
  esRechazoPorCuentaDePagoSinConfigurar,
} from "./cuentaDePagoDeLaTienda";
import { consultarEstadoFactura, hayDuplicadosEnBC } from "./estadoFacturaBCService";
import { isOnline, getSyncConfig } from "./syncService";
import { getCajaByNoOrId } from "./cajaService";
import { getTPVByNoOrId } from "./tpvService";
import { syncProductsStockByLocation } from "./productSyncService";
import { crearRegistro } from "../utils/registro";
import { traerElTimbrado } from "./claveDeHacienda";
import { leerCabeceraDeBC, esNumeroDeDocumentoUsable } from "../utils/cabeceraDeBC";
import { sustentoParaBC } from "../constants/creditMemoReasons";

// El envío de una factura son cuatro pasos contra Business Central --cabecera,
// líneas, pagos y contabilización-- y cada uno puede quedarse a medias. Saber
// en cuál se detuvo es la única forma de entender una factura atorada sin
// abrir la base local del terminal, así que estas líneas se conservan.
const registro = crearRegistro("factura");

// Refresca el stock de la tienda de la caja de esta factura, en background --
// no debe bloquear ni hacer fallar el sync de la factura. Sólo corre si hay
// internet y la sync automática está habilitada en configuración.
const refreshStockAfterInvoiceSync = async (cajaNo) => {
  try {
    if (!cajaNo || !isOnline()) return;

    const config = await getSyncConfig();
    if (!config.enabled) return;

    const caja = await getCajaByNoOrId(cajaNo);
    const tpv = caja?.TPV ? await getTPVByNoOrId(caja.TPV) : null;
    const locationCode = tpv?.locationCode;
    if (!locationCode) return;

    await syncProductsStockByLocation(locationCode);
  } catch (error) {
    registro.error("Error al refrescar stock tras sincronizar factura:", error);
  }
};

// El centro de responsabilidad de la tienda a la que pertenece la caja del
// documento (lo publica `ListaTPV`). Si no se puede resolver devuelve "" y la
// cabecera no lleva la clave: BC se queda con el suyo en vez de recibir vacío.
const centroDeResponsabilidadDeLaCaja = async (cajaNo) => {
  try {
    if (!cajaNo) return "";
    const caja = await getCajaByNoOrId(cajaNo);
    const tpv = caja?.TPV ? await getTPVByNoOrId(caja.TPV) : null;
    return tpv?.responsibilityCenter || "";
  } catch (error) {
    registro.error("Error al resolver el centro de responsabilidad de la caja:", error);
    return "";
  }
};

// Función para transformar las facturas al formato esperado por Business Central
export const transformInvoiceForBC = async (invoice, country) => {
  try {
    // El tipo, resuelto una sola vez y por el mismo sitio que las líneas y los
    // pagos. Antes cada bandera comparaba por su cuenta, y un tipo que no fuera
    // ninguno de los tres dejaba **las dos en falso**: ni factura ni abono, un
    // tercer estado que nadie había escrito. Ahora corta acá, con el motivo.
    const tipoParaBC = mapDocumentTypeForBC(invoice.Document_Type);
    const isInvoice = tipoParaBC === "Invoice";
    const isCreditMemo = tipoParaBC === "Credit Memo";

    const client = !isEmpty(invoice.Sell_to_Customer_No_)
      ? await db.clients
          .where("No")
          .equals(invoice.Sell_to_Customer_No_)
          .or("id")
          .equals(invoice.Sell_to_Customer_No_)
          .first()
      : null;

    // El descuento de la forma de pago sale del mismo sitio que el que ve el
    // cajero. Tenía acá su propia copia, sin la regla de que cualquier otro
    // descuento lo anula (decisión D5): con descuento de línea y una sola forma
    // de pago con descuento, la pantalla cobraba un total y a BC se le mandaba
    // otro. Por eso hacen falta también las líneas, no sólo los pagos.
    const paymentLines = await getInvoicePaymentLines(invoice);
    const invoiceLinesParaDescuento = await getInvoiceLines(invoice.id);
    const paymentDiscountPercentage = calcularDescuentoDeFormaDePago(
      invoiceLinesParaDescuento,
      paymentLines
    );

    // Preparar cabecera de la factura
    const customerNo =
      client?.No ||
      (typeof invoice.Sell_to_Customer_No_ === "string" ? invoice.Sell_to_Customer_No_ : undefined);
    const direccion2 = invoice.Direccion_2 || "";
    const header = {
      ...invoice,
      Id_TPV: invoice.Id_TPV || "",
      // La hora del alta, en hora local del terminal y en HH:mm:ss.
      //
      // Sólo se manda si el documento la tiene: los anteriores a este cambio no
      // la guardaron, y Business Central distingue la clave ausente --se queda
      // con su reloj, que es lo de hoy-- de una clave que no puede interpretar,
      // que responde con error. Mandar cadena vacía sería lo segundo.
      //
      // Vale igual para la factura y para la nota de crédito: las dos salen de
      // esta misma cabecera.
      ...(invoice.Hora_creacion ? { Hora_creacion: invoice.Hora_creacion } : {}),
      // BC sólo acepta `Invoice` / `Credit Memo` en este campo: la boleta de Perú
      // viaja como `Invoice` y se distingue por `SUNAT_Document` (más abajo).
      Document_Type: tipoParaBC,
      Posting_Date: invoice.Posting_Date,
      Document_Date: invoice.Document_Date,
      // Forzar a usar No del cliente; si no existe, dejaremos que la validación superior bloquee
      Sell_to_Customer_No_: customerNo,
      Bill_to_Customer_No_: customerNo,
      // Datos de facturación completos
      Bill_to_Name: invoice.Bill_to_Name,
      Bill_to_Address: invoice.Bill_to_Address,
      Bill_to_Address_2: direccion2,
      Bill_to_City: invoice.Bill_to_City || "",
      VAT_Registration_No_: invoice.VAT_Registration_No_ || "",
      Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || "",
      // Código de dirección de envío
      Ship_to_Code: invoice.Ship_to_Code || "01",
      // Tipo de documento de identificación fiscal
      Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || null,
      Fiscal_Invoice_Type: invoice.Fiscal_Invoice_Type || "Factura consumidor final",
      NCR: invoice.NCR || "",
      // Datos de envío (usamos los mismos que de facturación)
      Sell_to_Name: invoice.Bill_to_Name,
      Sell_to_Address: invoice.Bill_to_Address,
      Sell_to_Address_2: direccion2,
      Sell_to_City: invoice.Bill_to_City || "",
      Colegio: invoice.Colegio || "",
      Caja: invoice.Caja || invoice.caja || "",
      Turno: invoice.Turno || invoice.turno || "",
      // El cajero que atendio. Business Central ya lee esta clave y solo
      // escribe el campo si viene con algo, asi que un documento anterior a
      // este cambio no lo pisa. Acuerdo `contrato-traer-historicos.md`.
      ID_Cajero: invoice.ID_Cajero || "",
      // La forma de pago de la cabecera, tomada de la primera línea de pago.
      //
      // Business Central la exige al registrar --«Cód. forma pago debe tener un
      // valor en Cab. venta»-- y hasta ahora no la recibía nunca: el campo local
      // se llama `Payment_Method` y BC lee `Payment_Method_Code`, así que el
      // spread de `...invoice` no la hacía llegar.
      //
      // Del lado de BC hay una copia desde la línea de pago a la cabecera, pero
      // está encerrada en `if ConfSantillana.Country = 'PY'`, así que sólo cubre
      // a Paraguay. Mandarla desde aquí vale para los seis países y no depende
      // de que esa condición se abra.
      //
      // Con pago mixto se manda la primera, que es el mismo criterio que usa esa
      // copia de BC (`SalesLineT.FindFirst()`): la cabecera admite una sola, y el
      // desglose real vive en las líneas de pago.
      Payment_Method_Code:
        invoice.Payment_Method_Code ||
        paymentLines?.[0]?.Payment_Method ||
        invoice.Payment_Method ||
        "",
      // Incluir campos de descuento
      Dto: paymentDiscountPercentage,
      // Incluir campos de cupón
      Cupon: invoice.Cupon || "",
      Tipo_Detalle: invoice.Tipo_Detalle || "",
      No_Detalle: invoice.No_Detalle || "",
      // Incluir campo de campaña
      Campaign_No_: invoice.Campaign_No_ || "",
      No_Comprobante_Fiscal_Rel: invoice.No_Comprobante_Fiscal_Rel ?? "", // Incluir campos de Nota de creditos
      // Campos para Perú (Alumno)
      Student_Grade: invoice.Student_Grade || "",
      Student_Name: invoice.Student_Name || "",
      Student_DNI: invoice.Student_DNI || "",
      Student_Phone: invoice.Student_Phone || "",
    };
    // El spread de `...invoice` arrastra la fila entera de la base local: su
    // identificador de Dexie, las banderas de sincronización, lo que contestó
    // Business Central la vez anterior y lo que dejó puesto el formulario. Nada
    // de eso significa nada allá, y alguno hace daño --mandar `caja` y `Caja` a
    // la vez dejaba el turno en 0--. La lista y el porqué, en
    // `camposLocalesDeLaFactura.js`.
    quitarLosCamposLocales(header);

    // Centro de responsabilidad de la tienda: en todos los documentos (factura,
    // boleta y nota de crédito) y en todos los países. Sólo viaja si hay valor.
    delete header.Responsibility_Center;
    const centroDeResponsabilidad = await centroDeResponsabilidadDeLaCaja(header.Caja);
    if (centroDeResponsabilidad) header.Responsibility_Center = centroDeResponsabilidad;

    // Si es un abono, añadir la referencia a la factura original
    if (isCreditMemo) {
      header.Applies_to_Doc_Type = invoice.Applies_to_Doc_Type || "Invoice";
      header.Applies_to_Doc_No = invoice.Applies_to_Doc_No || "";
      header.EInvDocNoRelated = invoice.Applies_to_Doc_No || "";
      header.Cod_Tipo_Nota_Credito = invoice.Cod_Tipo_Nota_Credito || "";
      header.Tipo_Nota_Credito = invoice.Tipo_Nota_Credito || "";
      header.Sustento_Nota_Credito = sustentoParaBC(invoice.Sustento_Nota_Credito);
    }

    // El tipo de comprobante: solo Costa Rica y solo ventas -- factura y tiquete.
    //
    // Una venta de mostrador es un tiquete electronico --tipo 04 de Hacienda-- y
    // no una factura --tipo 01--. Los dos viajan en el mismo `Document_Type`
    // (`Invoice`): lo que los separa es este campo.
    //
    // **El tiquete tambien viaja explicito, y hace falta que viaje.** Se probo
    // lo contrario en Costa Rica DEV el 11/09/2026, mandando la misma venta tres
    // veces: sin la clave quedo `Factura` (VF15-009067), sin la clave y sin
    // `Fiscal_Invoice_Type` quedo `Factura` otra vez (VF15-009069), y con la
    // clave quedo `Tiquete` (VF15-009068). Business Central **no** lo deduce:
    // omitirla convierte cada venta de mostrador en una factura ante Hacienda
    // --tipo 01 en vez de tipo 04--. El dia que BC aprenda a deducirlo, esto se
    // vuelve a abrir; hasta entonces la clave se manda.
    //
    // La nota de credito si queda fuera, y eso esta medido: la entidad de abonos
    // ni siquiera publica este campo. BC resuelve el tipo del abono con el
    // documento que corrige --`EXCCRIReferencedDocumentType` sale «Tiquete
    // Electronico» o «Factura Electronica» segun corresponda-- y las diez notas
    // de credito que el terminal ya emitio en DEV lo confirman.
    //
    // Acuerdo `contrato-tipo-documento-electronico.md`, ronda 8, y su revision
    // del 11/09/2026.
    //
    // Se retira primero y se pone despues: el spread de `...invoice` arrastra el
    // campo local, asi que un documento con el tipo vacio o sin decidir haria
    // viajar una cadena vacia --un valor no admitido, que BC rechaza-- en vez de
    // omitir la clave. Mismo criterio que `caja` y `turno` mas arriba.
    delete header.Electronic_Document_Type;
    if (
      country?.code === COUNTRY_CODES.COSTARICA &&
      isInvoice &&
      invoice.Electronic_Document_Type
    ) {
      header.Electronic_Document_Type = invoice.Electronic_Document_Type;
    }

    // La ubicación y el correo del comprobante: sólo Costa Rica, factura y abono.
    //
    // CertificaE recorta la provincia, el cantón y el distrito del código postal
    // del documento; con él vacío, Hacienda rechaza (`VFR15-009033`, 13/09/2026).
    // El correo llega a `E-Mail-FE` por `Correo_FE`: `E_Mail`, que es lo que
    // hacía viajar el spread, BC no lo lee en la cabecera.
    //
    // Se retiran primero, como el tipo de comprobante: el spread arrastra los
    // campos locales, y una clave con un valor que no toca --un país que no es
    // `CR`, un código sin forma-- es peor que no mandarla, porque sin clave BC
    // toma la ficha del cliente. Acuerdo
    // `contrato-ubicacion-y-correo-fe-cri-2026-09-13.md`, filas T3 y T8.
    delete header.Sell_to_Post_Code;
    delete header.Sell_to_Country_Region_Code;
    delete header.Correo_FE;
    if (country?.code === COUNTRY_CODES.COSTARICA) {
      Object.assign(header, clavesDeUbicacionParaBC(invoice));
      const correo = String(invoice.E_Mail ?? "").trim();
      if (correo) header.Correo_FE = correo;
    }

    // Campos SUNAT (solo Perú)
    if (country?.code === COUNTRY_CODES.PERU) {
      header.CustomerDocumentType = invoice.Tipo_Documento_SrI || null;
      header.SUNAT_Document = mapUiToSystemType(invoice.Document_Type, country.code);

      if (isCreditMemo) {
        // Boleta o factura según el documento original, **no según la serie de
        // su número**: antes se miraba el prefijo (BP boleta, FP factura), que
        // eran las series de antes, y con las de hoy una boleta `B4B1-…` viajaba
        // como factura (`01`). `Applies_to_Doc_Type` tampoco sirve: se normaliza
        // siempre a "Invoice" al guardar la nota de crédito.
        const tipoDeLaReferencia = await tipoSunatDeLaReferencia(invoice);
        header.SUNAT_Document_Ref =
          tipoDeLaReferencia ||
          mapUiToSystemType(invoice.Applies_to_Doc_Type || "Invoice", country.code);
        header.Tipo_Motivo_NC = header.Cod_Tipo_Nota_Credito;
      }
    }

    // La factura y el abono salen de esta misma cabecera. Acá había un `else`
    // que lanzaba «Tipo de documento no soportado» sin decir cuál era ni qué se
    // esperaba; ahora el corte ocurre arriba, al resolver el tipo, y el motivo
    // lo nombra.
    return header;
  } catch (error) {
    registro.error("Error al transformar factura para BC:", error);
    throw error;
  }
};

// Transformar línea de factura para enviarla a Business Central
export const transformInvoiceLineForBC = (
  invoiceLine,
  documentNo,
  index,
  country = null,
  tipoDelDocumento = null
) => {
  // PRECIO SIN IVA - BC lo calcula todo nuevamente
  const unitPriceForBC = calculateBCUnitPrice({
    unitPrice: invoiceLine.Unit_Price,
    ivaPct: invoiceLine.PorIva,
    priceIncludesVAT: invoiceLine.Price_Includes_VAT === true,
    country,
  });

  // Crear objeto de línea de factura según formato API BC
  const line = {
    // El tipo lo pone la CABECERA, no la línea. En Business Central es parte de
    // la clave primaria de la fila y tiene que casar con un documento que ya
    // existe, así que no es un dato propio de la línea: es derivado. Leerlo del
    // hijo es lo que permitía que divergieran, y el `|| "Invoice"` que había acá
    // lo sustituía en silencio cuando faltaba --dejando la fila colgando de otro
    // documento o de ninguno--. Se conserva el de la línea como respaldo para
    // los borradores anteriores a que la cabecera se pasara.
    Document_Type: mapDocumentTypeForBC(tipoDelDocumento ?? invoiceLine.Document_Type),
    Document_No_: documentNo,
    // La clave primaria local, no la posición.
    //
    // Antes era `(index + 1) * 10000`, la posición dentro de las líneas no
    // borradas. Si entre un intento y el reintento el cajero borraba o agregaba
    // una línea, todas las posteriores se renumeraban y Business Central las
    // insertaba como nuevas en vez de reemplazarlas: líneas duplicadas.
    //
    // El id de Dexie es inmutable, único dentro del documento y creciente, así
    // que el orden en pantalla no cambia. Comprometido en el acuerdo 11 (§3.2).
    Line_No_: invoiceLine.id ?? invoiceLine.Line_No ?? (index + 1) * 10000,
    Type: invoiceLine.Type || "Item",
    No_: invoiceLine.No || "",
    Description: invoiceLine.Description || "",
    Quantity: roundQuantity(invoiceLine.Quantity || 0),
    Unit_Price: unitPriceForBC,
    Line_Discount_: invoiceLine.Line_Discount_ || 0,
    PorIva: invoiceLine.PorIva || 0,
  };

  // Filtrar campos con valores vacíos
  return Object.fromEntries(Object.entries(line).filter(([_, value]) => value !== ""));
};

// Transformar línea de pago para enviarla a Business Central
export const transformInvoicePaymentLineForBC = (
  paymentLine,
  documentNo,
  index,
  tipoDelDocumento = null
) => {
  // Crear objeto de línea de pago según formato API BC
  const line = {
    // Mismo criterio que en la línea: lo pone la cabecera, porque el tipo es
    // parte de la clave del pago y tiene que casar con su documento.
    Document_Type: mapDocumentTypeForBC(tipoDelDocumento ?? paymentLine.Document_Type),
    Document_No_: documentNo,
    // Mismo criterio que las líneas, y acá hacía más falta: el Line_No local
    // de un pago se asignaba como `paymentLines.length + 1`, así que quitar el
    // pago 2 de 3 y agregar otro volvía a dar 3. Ni estable ni único.
    Line_No: paymentLine.id ?? paymentLine.Line_No ?? (index + 1) * 10000,
    Payment_Method: paymentLine.Payment_Method || "",
    Amount: paymentLine.Amount || 0,
    Dto: paymentLine.Dto || 0,
    Dto_Amount: paymentLine.Dto_Amount || 0,
  };

  // Filtrar campos con valores vacíos
  return Object.fromEntries(Object.entries(line).filter(([_, value]) => value !== ""));
};

/**
 * El mensaje de un fallo de sincronización, con el nombre del documento que es.
 *
 * Todos decían «la factura», también al sincronizar un abono o un tiquete: el
 * cajero leía «No se pudo crear la cabecera de la factura» delante de una nota
 * de crédito, y lo primero que uno hace con un mensaje así es buscar una factura
 * que no existe.
 *
 * @param {string} code     El código del fallo.
 * @param {string} details  Lo que dijo Business Central, si dijo algo.
 * @param {object} invoice  El documento, para nombrarlo como es.
 */
const formatSyncError = (code, details = "", invoice = null) => {
  const doc = elDocumento({
    documentType: invoice?.Document_Type,
    tipoDeComprobante: invoice?.Electronic_Document_Type,
  });
  // Al principio de la frase el artículo va en mayúscula, y en medio no.
  const Doc = doc.charAt(0).toUpperCase() + doc.slice(1);

  const messages = {
    ALREADY_SYNCING: `${Doc} ya se está sincronizando en otro proceso.`,
    CLIENT_NOT_SYNCED: "El cliente no está sincronizado en Business Central.",
    // Sin participio: los cuatro nombres no comparten género, y «no salió» vale
    // para los cuatro. Y sin repetir lo que la pantalla ya dice alrededor.
    DATOS_INVALIDOS: `Hay datos que Hacienda rechazaría, así que ${doc} no salió del terminal.`,
    // Panamá: además de la DGI, lo que la migración de Panamá no deja registrar.
    DATOS_INVALIDOS_DGI: `Hay datos que la DGI o Business Central rechazarían, así que ${doc} no salió del terminal.`,
    DUPLICADO_EN_BC: "Hay más de un documento con este identificador en Business Central.",
    SIN_CORREO: `${Doc} no salió del terminal: le falta el correo electrónico del cliente.`,
    HEADER_CREATION_FAILED: `No se pudo crear la cabecera de ${doc} en Business Central.`,
    LINES_SYNC_FAILED: `No se pudieron enviar las líneas de ${doc}.`,
    NO_CAJA: `No se puede sincronizar ${doc} sin caja.`,
    NO_LINES: `No se puede sincronizar ${doc} sin líneas.`,
    PAGOS_NO_CUBREN: `${Doc} no se registró: los pagos que tiene Business Central no cubren su total.`,
    PAYMENTS_SYNC_FAILED: "No se pudieron enviar las líneas de pago.",
    POSTING_FAILED: `${Doc} se sincronizó, pero no se pudo contabilizar en Business Central.`,
    RECHAZO_DEFINITIVO: `Business Central rechazó ${doc} por un motivo que reintentar no arregla.`,
    UNKNOWN_ERROR: `Ocurrió un error inesperado al sincronizar ${doc}.`,
  };

  let message = messages[code] || messages.UNKNOWN_ERROR;

  // El detalle, cuando lo hay y cuando sirve. Es por donde se colaba «Network
  // Error» hasta el cajero: el motivo de Business Central sí se enseña --es lo
  // que él puede corregir-- pero el nombre interno de un fallo de red, no.
  if (details) {
    message += ` ${limpiarMotivoTecnico(details)}`;
  }

  return { code, message, details, success: false };
};

/**
 * Deja el documento listo para volver a mandar sus pagos, con el motivo a la vista.
 *
 * Vuelve a la etapa `lines` --la cabecera y las líneas ya están en Business
 * Central-- y queda pendiente, así que la corrida siguiente reenvía los pagos y
 * vuelve a intentar el registro. Sin reintento en la misma corrida: con los
 * mismos pagos, el resultado sería el mismo. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H1.
 *
 * @param {object} invoice El documento.
 * @param {string} motivo  Lo que dijo Business Central, o lo que tiene cobrado.
 */
const volverAMandarLosPagos = async (invoice, motivo) => {
  const error = formatSyncError("PAGOS_NO_CUBREN", motivo, invoice);

  await db.invoices.update(invoice.id, {
    syncStage: "lines",
    pendingSync: 1,
    posted: 0,
    syncError: true,
    syncErrorMessage: error.message,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(error.message);

  return { ...error };
};

/**
 * Deja el documento listo para volver a mandar sus líneas, con el motivo a la vista.
 *
 * Vuelve a la etapa `header` --la cabecera está en Business Central, las líneas
 * no son las del terminal-- y queda pendiente, así que la corrida siguiente
 * reenvía las líneas con `"Reemplazar_Lineas": true`. No se mandan pagos ni se
 * registra: un documento con líneas que no son las del terminal se timbraría
 * por otro importe. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1 · TPV.
 *
 * @param {object} invoice El documento.
 * @param {string} motivo  El aviso de `lineasAntesDeCobrar.js`.
 */
const volverAMandarLasLineas = async (invoice, motivo) => {
  await db.invoices.update(invoice.id, {
    syncStage: "header",
    pendingSync: 1,
    posted: 0,
    syncError: true,
    syncErrorMessage: motivo,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(motivo);

  return { code: "LINEAS_NO_CUADRAN", message: motivo, details: "", success: false };
};

/**
 * Deja el documento pendiente porque Business Central tiene cobrado **más** que
 * el importe del documento.
 *
 * Sin tocar la etapa, a diferencia de `volverAMandarLosPagos`: reenviar los
 * pagos no retira el que sobra --los pagos no se retiran, acuerdo del 12/09--,
 * así que volver atrás sólo repetiría el envío sin arreglar nada. Lo resuelve
 * quien quite el pago allá, y entonces la corrida siguiente registra.
 *
 * Registrar así no sería sólo un descuadre del cajón: el apunte del cobro va
 * por lo cobrado y la factura por su importe, de modo que la diferencia le
 * queda al cliente como saldo a favor.
 *
 * @param {object} invoice El documento.
 * @param {string} motivo  El aviso de `cobroAntesDeRegistrar.js`.
 */
const dejarPendientePorElCobroQueSobra = async (invoice, motivo) => {
  await db.invoices.update(invoice.id, {
    pendingSync: 1,
    posted: 0,
    syncError: true,
    syncErrorMessage: motivo,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(motivo);

  return { code: "COBRO_QUE_SOBRA", message: motivo, details: "", success: false };
};

/**
 * Deja el documento pendiente, sin registrar, porque Business Central no tiene
 * los mismos pagos que el terminal.
 *
 * Sin reintento en la corrida. Si BC tiene **más**, reenviar no retira nada
 * --los pagos no se retiran, acuerdo del 12/09-- y la etapa no se toca: sólo lo
 * arregla quien retire el pago allá. Si tiene **menos**, se vuelve a `lines`
 * para que la corrida siguiente reenvíe los pagos, igual que
 * `volverAMandarLosPagos`. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`,
 * H1 · TPV.
 *
 * @param {object} invoice El documento.
 * @param {{enBC: number, enTerminal: number}} pagos Lo que devolvió `pagosQueNoCuadranEnBC`.
 */
const dejarPendientePorLosPagos = async (invoice, pagos) => {
  const motivo = avisoDePagosQueNoCuadran(pagos);

  await db.invoices.update(invoice.id, {
    ...(pagos.enBC < pagos.enTerminal ? { syncStage: "lines" } : {}),
    pendingSync: 1,
    posted: 0,
    syncError: true,
    syncErrorMessage: motivo,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(motivo);

  return { code: "PAGOS_NO_CUADRAN", message: motivo, details: "", success: false };
};

/**
 * Deja el documento pendiente porque a la tienda le falta la cuenta de pago de
 * esa forma de pago en Business Central.
 *
 * **Pendiente y no fuera de la cola**, a diferencia de `ABONO_EXCEDE`: esto lo
 * arregla quien configura Business Central añadiendo la fila, y en cuanto
 * exista, el mismo documento se registra sin que nadie toque la caja. Es el
 * caso de `SERIE_NO_CONFIGURADA`.
 *
 * La etapa no se toca: la cabecera, las líneas y los pagos ya están en Business
 * Central y son los del terminal --el `Error` de `CuentaPago` deshizo el
 * registro, no el alta--. Lo único que falta es volver a pedir que se registre.
 *
 * @param {object} invoice El documento.
 * @param {string} motivo  El `error.message` del 400 de `Microsoft.NAV.post`.
 */
const dejarPendientePorLaCuentaDePago = async (invoice, motivo) => {
  const aviso = avisoDeLaCuentaDePagoSinConfigurar(motivo);

  await db.invoices.update(invoice.id, {
    pendingSync: 1,
    posted: 0,
    syncError: true,
    syncErrorMessage: aviso,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(aviso);

  return { code: "CUENTA_PAGO_NO_CONFIGURADA", message: aviso, details: "", success: false };
};

/**
 * El error de un rechazo que reintentar no arregla, con su explicación.
 *
 * `ABONO_EXCEDE` y `CLIENTE_BLOQUEADO` llevan la suya, para que el cajero sepa
 * qué pasó y qué hacer; el resto, la genérica de `RECHAZO_DEFINITIVO`. En todos
 * los casos el texto de Business Central va detrás.
 */
const rechazoQueNoSeReintenta = (motivo, invoice) => {
  const error = formatSyncError("RECHAZO_DEFINITIVO", motivo, invoice);
  if (esRechazoPorAbonoQueExcede(motivo)) {
    return { ...error, message: avisoDelAbonoQueExcede(motivo) };
  }
  if (esRechazoPorClienteBloqueado(motivo)) {
    return { ...error, message: avisoDelClienteBloqueado(motivo) };
  }
  return error;
};

/**
 * Saca de la cola la nota de crédito que Business Central no registró por
 * `ABONO_EXCEDE`.
 *
 * Sin modal de «Error Business Central» y sin tocar la etapa: el documento no se
 * registró y no hay número que recuperar, y reintentar el registro daría el
 * mismo rechazo. El borrador que queda en BC lo retira Datos. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
 *
 * @param {object} invoice El documento.
 * @param {string} motivo  El `error.message` del 400 de `Microsoft.NAV.post`.
 */
const dejarFueraPorAbonoQueExcede = async (invoice, motivo) => {
  const error = rechazoQueNoSeReintenta(motivo, invoice);

  await db.invoices.update(invoice.id, {
    pendingSync: 0,
    posted: 0,
    syncError: true,
    syncErrorMessage: error.message,
    lastSynced: getCurrentDateTimeISO(),
  });
  registro.warn(error.message);

  return { ...error };
};

// Facturas con una sincronización en curso en este momento (por id). Evita que
// dos triggers solapados (intervalo, evento 'online', sync manual, post-cierre
// de caja, etc.) procesen la misma factura a la vez -- si corren en paralelo,
// ambos pueden pasar el chequeo "¿ya existe en BC por Id_TPV?" antes de que
// cualquiera termine de crearla, duplicando la factura en BC con el mismo
// Id_TPV pero distinto No.
const invoicesSyncingNow = new Set();

// Sincronizar una factura a Business Central
const syncInvoiceToBCInternal = async (invoice, country) => {
  try {
    await db.invoices.update(invoice.id, { syncError: null, syncErrorMessage: null });
    // -----------------------------
    // 0. Verificar cliente
    // -----------------------------
    let client =
      !isEmpty(invoice.Sell_to_Customer_No_) || !isEmpty(invoice.clientLocalId)
        ? await getClientByNoOrId(invoice.Sell_to_Customer_No_ || invoice.clientLocalId)
        : null;

    if ((!client || !client.No || client.pendingSync) && !isEmpty(client?.name)) {
      // Intentar refrescar clientes por si se sincronizó recientemente
      await syncClients();
      client = await getClientByNoOrId(invoice.Sell_to_Customer_No_ || invoice.clientLocalId);
      if (!client || !client.No) {
        let error = formatSyncError("CLIENT_NOT_SYNCED", "", invoice);

        return {
          ...error,
          pendingClientSync: true,
          clientId: invoice.clientLocalId || invoice.Sell_to_Customer_No_,
        };
      }
    }

    // Sin caja no se manda.
    //
    // El Id_TPV lleva la caja en el primer segmento --es lo que lo hace único
    // entre terminales--, así que una factura sin caja no puede componerlo, y
    // Business Central la recibiría con el prefijo reservado `SIN-`: entra,
    // pero ningún guardia puede reconciliarla después. Un reintento la
    // duplicaría. Comprometido en el acuerdo 11 (§3.1).
    if (!(invoice.Caja || invoice.caja)) {
      const error = formatSyncError("NO_CAJA", "", invoice);
      await db.invoices.update(invoice.id, {
        syncError: true,
        syncErrorMessage: error.message,
        pendingSync: 0,
        syncing: 0,
        syncStartTime: null,
        lastModified: getCurrentDateTimeISO(),
      });
      return { ...error };
    }

    // -----------------------------
    // 1. Obtener líneas y pagos
    // -----------------------------
    const invoiceLines = await getInvoiceLines(invoice.id);
    const paymentLines = await getInvoicePaymentLines(invoice);

    //No debe sincronizas si lineas esta vacio
    if (!invoiceLines || !paymentLines || invoiceLines.length === 0 || paymentLines.length === 0) {
      let error = formatSyncError("NO_LINES", "", invoice);
      await db.invoices.update(invoice.id, {
        No: null,
        syncStage: "none",
        syncError: true,
        syncErrorMessage: error.message,
        pendingSync: 0,
        lastSynced: getCurrentDateTimeISO(),
      });

      return {
        ...error,
      };
    }

    // Sin correo del cliente, en Costa Rica no sale nada: ni tiquete, ni factura,
    // ni nota de crédito. Regla de Santillana del 13/09/2026, en el mismo punto y
    // con el mismo tratamiento que lo que Hacienda rechazaría. Ver
    // `costarica/correoDelCliente.js`.
    if (faltaElCorreoDelCliente(invoice, country?.code)) {
      throw formatSyncError("SIN_CORREO", "", invoice);
    }

    // Lo que Hacienda rechazaría, antes de que nada salga del terminal.
    //
    // Vive acá, en el tronco, y no en el cierre fiscal de Costa Rica, porque a
    // Business Central se llega por cuatro caminos: el cierre de la venta, el
    // botón de sincronizar de la fila, la sincronización automática de fondo y
    // la de después del cierre de caja. Puesto en uno solo, los otros tres
    // seguían mandando documentos que se iban a rechazar --y un comprobante
    // rechazado ya está emitido: hay que rehacerlo entero--.
    //
    // Sólo se comprueba lo determinista, que son los niveles 1 y 2 de Hacienda:
    // estructura y formato. Que una cédula bien formada exista de verdad es
    // nivel 3, y eso no se puede saber sin preguntarle a Hacienda.
    //
    // El IVA del cliente va aparte porque necesita la base local: un grupo de IVA
    // que le aplica a una línea otro porcentaje que al cliente de mostrador de la
    // tienda termina en un comprobante que Hacienda rechaza (`VFR15-009039`).
    // Sin cliente de mostrador en la tienda no se bloquea, pero se anota: sin él
    // la guarda no tiene contra qué comparar. Acuerdo
    // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H3.
    const delIva = await revisarIvaDelDocumento({
      documento: invoice,
      lineas: invoiceLines,
      countryCode: country?.code,
      ficha: client,
    });
    if (delIva.sinClientePorDefecto) {
      anotarAviso(
        "facturas",
        `${invoice.No || invoice.Pre_Assigned_No || invoice.id}: ${delIva.motivo}`
      );
    }
    const reparos = [
      ...loQueRechazaria(
        revisarParaHacienda({
          cliente: invoice,
          // La ubicación que la cabecera no lleve, BC la toma de la ficha.
          ficha: client,
          lineas: invoiceLines,
          esTiquete: invoice.Electronic_Document_Type === TIPOS_DE_COMPROBANTE.TIQUETE,
          esNotaDeCredito: invoice.Document_Type === "Credit Memo",
          countryCode: country?.code,
        })
      ),
      ...loQueRechazaria(delIva.avisos),
    ];
    if (reparos.length) {
      throw formatSyncError(
        "DATOS_INVALIDOS",
        reparos.map((r) => `${r.campo}: ${r.aviso}`).join(" · "),
        invoice
      );
    }

    // Lo mismo para Panamá, en el mismo punto y por la misma razón: los cuatro
    // caminos a Business Central pasan por aquí. La factura electrónica de Panamá
    // se arma con la ficha del cliente --tipo de cliente, DV, ubicación, correo--,
    // así que se revisa la ficha y no sólo la cabecera. Ver
    // `panama/validacionDeLaDGI.js` y `12 - Panamá/campos-excpan-tpv-2026-09-17.md`.
    const reparosDeLaDGI = loQueRechazaria(
      revisarParaLaDGI({
        documento: invoice,
        ficha: client,
        lineas: invoiceLines,
        esNotaDeCredito: invoice.Document_Type === "Credit Memo",
        countryCode: country?.code,
      })
    );
    if (reparosDeLaDGI.length) {
      throw formatSyncError(
        "DATOS_INVALIDOS_DGI",
        reparosDeLaDGI.map((r) => `${r.campo}: ${r.aviso}`).join(" · "),
        invoice
      );
    }

    // La misma regla que la pantalla y que la cabecera. Antes acá se guardaba
    // el MÁXIMO de todas las formas de pago, sin mirar cuántas había ni si
    // alguna línea traía descuento: un tercer criterio para el mismo concepto.
    const maxDiscount = calcularDescuentoDeFormaDePago(invoiceLines, paymentLines);

    // Calcular el total de la factura con IVA específico por producto
    const lineTotals = invoiceLines.map((line) => {
      const quantity = line.Quantity || 0;
      const unitPrice = line.Unit_Price || 0;
      const discount = line.Line_Discount_ || 0;
      const discountAmount = unitPrice * (discount / 100);
      const finalPrice = unitPrice - discountAmount;
      const lineSubtotal = quantity * finalPrice;

      // Aplicar IVA específico del producto
      const lineIva = line.PorIva || 0;
      const lineVat = lineSubtotal * (lineIva / 100);

      return { lineSubtotal, lineVat };
    });

    const decimals = resolveMoneyDecimals(country);
    const subtotal = sumMoney(lineTotals, (l) => l.lineSubtotal, decimals);
    const vat = sumMoney(lineTotals, (l) => l.lineVat, decimals);
    const total = roundMoney(subtotal + vat, decimals);
    const importeDescuento =
      maxDiscount > 0 ? roundMoney(total * (maxDiscount / 100), decimals) : 0;

    // Actualizar los campos Dto e importeDto en la factura
    await db.invoices.update(invoice.id, {
      Dto: maxDiscount,
      importeDto: importeDescuento,
    });

    // Recargar la factura actualizada
    let updatedInvoice = await db.invoices.get(invoice.id);

    // -----------------------------
    // 2. Preparar cabecera para BC
    // -----------------------------
    const invoiceForBC = await transformInvoiceForBC(updatedInvoice, country);

    if (country.code === COUNTRY_CODES.PERU) {
      await db.invoices.update(invoice.id, {
        CustomerDocumentType: invoiceForBC.CustomerDocumentType || null,
        SUNAT_Document: invoiceForBC.SUNAT_Document || "",
        SUNAT_Document_Ref: invoiceForBC.SUNAT_Document_Ref || "",
        Tipo_Motivo_NC: invoiceForBC.Tipo_Motivo_NC || "",
      });
      updatedInvoice = await db.invoices.get(invoice.id);
    }

    const invoiceData = {
      data: JSON.stringify({ Sales_Headers: [invoiceForBC] }),
    };

    // -----------------------------
    // Verificar si la factura ya existe en BC
    // -----------------------------
    if (updatedInvoice.Id_TPV) {
      // Primero se le pregunta a Business Central qué sabe de esta factura.
      //
      // El terminal reanuda por su propio `syncStage`, que es local y puede
      // mentir: si la respuesta de un paso se perdió, el paso pudo completarse
      // igual. Y sobre todo, ese estado no sabe si en BC hay **más de un**
      // documento con el mismo identificador -- reanudar sobre uno de dos
      // duplicados escribe las líneas en el equivocado.
      //
      // Acuerdo 11 (§3.5): no reanudar a ciegas si `coincidencias > 1`.
      const estadoEnBC = await consultarEstadoFactura({
        tienda: updatedInvoice.Tienda || updatedInvoice.tienda,
        caja: updatedInvoice.Caja || updatedInvoice.caja,
        idTpv: updatedInvoice.Id_TPV,
        documentType: mapDocumentTypeForBC(updatedInvoice.Document_Type),
      });

      if (hayDuplicadosEnBC(estadoEnBC)) {
        // Se para y se deja a la vista. No se libera el pendiente: esta factura
        // necesita que alguien mire cuál de los documentos es el bueno.
        const error = formatSyncError(
          "DUPLICADO_EN_BC",
          `${estadoEnBC.coincidencias} documentos con el identificador ${updatedInvoice.Id_TPV}`,
          invoice
        );
        registro.error(error.message);
        await db.invoices.update(invoice.id, {
          syncError: true,
          syncErrorMessage: error.message,
          syncing: 0,
          syncStartTime: null,
          lastModified: getCurrentDateTimeISO(),
        });
        return { ...error };
      }

      const existingInvoiceInBC = await findInvoiceInBCByIdTpv(
        updatedInvoice.Id_TPV,
        mapDocumentTypeForBC(updatedInvoice.Document_Type)
      );

      if (existingInvoiceInBC) {
        await reconcileInvoiceWithBC(updatedInvoice, existingInvoiceInBC);
        updatedInvoice = await db.invoices.get(invoice.id);

        // Si apareció ya registrada, la reconciliación la cerró: no hay nada
        // que enviar y seguir sería reescribir un documento fiscal emitido.
        if (existingInvoiceInBC.registrada) {
          return { success: true, invoice: updatedInvoice, yaRegistrada: true };
        }
      }
    }

    // -----------------------------
    // 3. Crear CABECERA si no existe
    // -----------------------------
    let syncStage = updatedInvoice.syncStage || "none";
    let preAssignedDocNo = updatedInvoice.Pre_Assigned_No || "";
    let systemId = updatedInvoice.systemId || "";

    if (!preAssignedDocNo) {
      if (syncStage === "none") {
        await db.invoices.update(invoice.id, {
          syncStage,
        });
      }

      const headerResponse = await sendToBusinessCentral(
        "Importaciones_insertaFacturasVenta",
        invoiceData,
        true
      );

      if (!headerResponse?.value) {
        const motivo = motivoDeRechazoBC(headerResponse) || "BC no devolvió número de documento";
        // Un rechazo que reintentar no arregla sale de la cola con su motivo a la
        // vista; el resto se reintenta como siempre. Ver `rechazoDefinitivo.js`.
        if (await esRechazoDefinitivo(motivo, updatedInvoice)) {
          throw rechazoQueNoSeReintenta(motivo, invoice);
        }
        throw formatSyncError("HEADER_CREATION_FAILED", motivo, invoice);
      }

      // Se lee lo que llegó, sin preguntar el país.
      //
      // La acción devuelve un solo escalar, así que cuando hizo falta devolver
      // también el `System Id` se serializó un JSON dentro de él. Antes eso se
      // desempaquetaba sólo para Paraguay y Honduras, y el día que la acción de
      // otro país contestó igual el terminal mandó el JSON entero como
      // `Document_No_` de cada línea: un campo de 20 caracteres. Le pasó a Perú
      // el 28 de agosto de 2026. Ver utils/cabeceraDeBC.js.
      const cabecera = leerCabeceraDeBC(headerResponse.value);

      preAssignedDocNo = cabecera.No;
      if (cabecera.SystemId) systemId = cabecera.SystemId;

      // Y no se sigue con un número que no sirve para relacionar nada: cortar
      // acá deja el motivo a la vista en el terminal, en vez de un desbordado
      // del otro lado que no dice de dónde vino.
      if (!esNumeroDeDocumentoUsable(preAssignedDocNo)) {
        throw formatSyncError(
          "HEADER_CREATION_FAILED",
          `Business Central devolvió un número de documento que no se puede usar: ${JSON.stringify(
            headerResponse.value
          )}`,
          invoice
        );
      }

      // Guardar inmediatamente para evitar duplicados
      syncStage = "header";
      await db.invoices.update(invoice.id, {
        No: null,
        Pre_Assigned_No: preAssignedDocNo,
        systemId: systemId,
        syncStage,
        syncError: null,
        syncErrorMessage: null,
        pendingSync: 1,
        lastSynced: getCurrentDateTimeISO(),
      });

      registro.log(`Cabecera creada en BC con Pre_Assigned_No ${preAssignedDocNo}`);

      refreshStockAfterInvoiceSync(updatedInvoice.caja);
    }

    // -----------------------------
    // 4. Enviar LÍNEAS si no se hizo
    // -----------------------------
    if (syncStage === "none" || syncStage === "header") {
      const transformedLines = invoiceLines.map((line, index) =>
        transformInvoiceLineForBC(
          line,
          preAssignedDocNo,
          index,
          country,
          mapDocumentTypeForBC(invoice.Document_Type)
        )
      );
      // `Reemplazar_Lineas` en cada envío, en la raíz junto a `Sales_Lines`.
      //
      // Desde la 1.0.4.112, Business Central retira del borrador las líneas que
      // no lleguen en el envío sólo si la clave viene: un terminal viejo, o uno
      // que partiera el lote, perdería líneas sin enterarse. Éste manda siempre
      // todas las líneas no borradas en una sola llamada, así que lo que no
      // llega es lo que se quitó en el terminal. La BC de hoy ignora la clave
      // sin error (medido en DEV sobre `VF15-009103`), y lo que no corrija lo
      // detecta `lineasAntesDeCobrar.js`. Acuerdo
      // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1.
      const linesData = {
        data: JSON.stringify({ Sales_Lines: transformedLines, Reemplazar_Lineas: true }),
      };
      registro.log("Enviando líneas de factura");
      const responseLineas = await sendToBusinessCentral(
        "Importaciones_insertaLineasFacturasVenta",
        linesData,
        true
      );

      // Esta comprobación miraba `responseLineas.data`, un campo que la
      // respuesta no tiene: la condición nunca se cumplía y un rechazo de las
      // líneas se daba por bueno. La cabecera quedaba en Business Central sin
      // líneas, la factura se marcaba sincronizada, y nadie decía nada.
      //
      // Un rechazo definitivo --`ABONO_EXCEDE`, la puerta 1 de Business Central
      // al importar las líneas de una nota de crédito-- sale de la cola con su
      // motivo y sin tocar la etapa; el resto se reintenta, `LINEA_REPETIDA`
      // incluido. El prefijo se lee anclado, como en la cabecera. Acuerdo
      // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
      const rechazoDeLineas = motivoDeRechazoBC(responseLineas);
      if (rechazoDeLineas) {
        if (await esRechazoDefinitivo(rechazoDeLineas, updatedInvoice)) {
          throw rechazoQueNoSeReintenta(rechazoDeLineas, invoice);
        }
        throw formatSyncError("LINES_SYNC_FAILED", rechazoDeLineas, invoice);
      }

      // Un `Ok` con cola es un aviso de BC --por ejemplo, que el PorIva que
      // mandamos difiere del que aplicó: señal de catálogo desactualizado--. Se
      // anota en la consola de sincronización, que es el lector comprometido en
      // el contrato de idempotencia: sin él, el aviso nacería muerto.
      const avisoDeLineas = avisoTrasElOk(responseLineas);
      if (avisoDeLineas) {
        anotarAviso("facturas", `líneas de ${preAssignedDocNo}: ${avisoDeLineas}`);
      }

      syncStage = "lines";
      await db.invoices.update(invoice.id, {
        syncError: null,
        syncErrorMessage: null,
        syncStage,
        lastSynced: getCurrentDateTimeISO(),
      });

      for (const line of invoiceLines) {
        await db.invoiceLines.update(line.id, {
          lastSynced: getCurrentDateTimeISO(),
          pendingSync: 0,
        });
      }
      registro.log("Líneas enviadas");
    }

    // -----------------------------
    // 5. Enviar PAGOS si no se hizo
    // -----------------------------
    // El importe del pago se compone con el número de Business Central, no con
    // el nuestro.
    //
    // BC recalcula precios e IVA por su cuenta --el comentario del propio
    // terminal lo dice: «BC lo calcula todo nuevamente»--, así que su total
    // puede no coincidir al céntimo con el local. Y la guarda `PAGO_INCOMPLETO`
    // compara la suma de los pagos contra **su** total, no contra el nuestro:
    // preguntarle antes de mandar los pagos hace que no pueda dispararse por una
    // diferencia de cálculo.
    //
    // `estadoFactura` devuelve **404 en las cuatro producciones**, que van entre
    // 75 y 78 versiones por detrás de los ambientes donde se prueba. Por eso
    // esto tolera la ausencia y compone como siempre: sin la tolerancia, el
    // abono se rompería justo donde hay cajas cobrando. `consultarEstadoFactura`
    // ya devuelve `null` ante cualquier fallo, y `null` significa «no sé», no
    // «cero». Acuerdo `contrato-nota-de-credito-sobre-historicos`, ronda 3.
    //
    // Se conserva el estado entero, no sólo el importe: `lineasAntesDeCobrar.js`
    // necesita también `numLineas` y `toleranciaPagos` (H1 · TPV del 14/09).
    const estadoTrasLasLineas = await (async () => {
      try {
        return await consultarEstadoFactura({
          tienda: updatedInvoice.Tienda || updatedInvoice.tienda,
          caja: updatedInvoice.Caja || updatedInvoice.caja,
          idTpv: updatedInvoice.Id_TPV,
          documentType: mapDocumentTypeForBC(updatedInvoice.Document_Type),
        });
      } catch (error) {
        return null;
      }
    })();
    // En magnitud: para una nota de crédito, Business Central puede contestar
    // este importe en negativo, y con la comparación a secas caía en `null`. Lo
    // que se pierde entonces no es un aviso: se saltan **en silencio** el ajuste
    // de la última forma de pago y la comprobación de descuadre, que son las dos
    // cosas para las que se pidió este dato.
    //
    // `lineasAntesDeCobrar.js` ya leía **este mismo campo** con `Math.abs`, así
    // que la prueba de que puede venir negativo estaba a dos funciones de aquí.
    const importeSegunBC = Math.abs(Number(estadoTrasLasLineas?.importeLineas));
    const totalSegunBC =
      Number.isFinite(importeSegunBC) && importeSegunBC > 0 ? importeSegunBC : null;

    // Las líneas deducidas no viajan. Se crean para que una nota de crédito
    // sobre una factura histórica sin detalle de pago pueda cumplir la regla de
    // «pendiente 0», pero en Business Central una fila de `Detalle Pago Factura`
    // se contabiliza como un `Refund` contra la cuenta de esa forma de pago: la
    // mandaría a sacar dinero de la caja de hoy por una devolución de hace años.
    // Acuerdo `contrato-nota-de-credito-sobre-historicos`, ronda 3.
    const pagosQueViajan = (paymentLines ?? []).filter((line) => !line?.deducida);
    const deducidas = (paymentLines?.length ?? 0) - pagosQueViajan.length;
    if (deducidas > 0) {
      anotarAviso(
        "facturas",
        `${preAssignedDocNo}: ${deducidas} forma(s) de pago deducida(s) del encabezado, no se envían a Business Central`
      );
    }

    // Entre las líneas y los pagos: si Business Central no tiene las líneas del
    // terminal, no se mandan pagos ni se registra, y la corrida siguiente
    // reenvía las líneas. Corre antes del ajuste de la última forma de pago, y
    // una diferencia de hasta 0,05 nunca bloquea, así que ese ajuste sigue igual.
    // Se compara contra todas las líneas de pago, deducidas incluidas, y actúa
    // también cuando ninguna viaja. Ver `lineasAntesDeCobrar.js`. Acuerdo
    // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1 · TPV.
    const lineasQueNoCuadran = lineasQueNoCuadranEnBC(
      estadoTrasLasLineas,
      { lineasLocales: invoiceLines, pagosLocales: paymentLines },
      country
    );
    if (lineasQueNoCuadran) {
      return await volverAMandarLasLineas(
        invoice,
        avisoDeLineasQueNoCuadran(lineasQueNoCuadran, country)
      );
    }

    if (pagosQueViajan.length > 0) {
      if (syncStage === "none" || syncStage === "header" || syncStage === "lines") {
        const transformedPaymentLines = pagosQueViajan.map((line, index) =>
          transformInvoicePaymentLineForBC(
            line,
            preAssignedDocNo,
            index,
            mapDocumentTypeForBC(invoice.Document_Type)
          )
        );

        // Si BC contestó su total, la diferencia se ajusta en la ÚLTIMA línea y
        // sólo si es pequeña. Repartirla entre todas movería importes que el
        // cajero sí tecleó; absorber una diferencia grande escondería un
        // problema de catálogo en vez de delatarlo --que es justo lo que el
        // aviso del `PorIva` existe para hacer--.
        if (totalSegunBC !== null && transformedPaymentLines.length > 0) {
          const decimalesDelPago = resolveMoneyDecimals(country);
          const sumaEnviada = sumMoney(transformedPaymentLines, (l) => l.Amount, decimalesDelPago);
          const diferencia = subtractMoney(totalSegunBC, sumaEnviada, decimalesDelPago);

          if (diferencia !== 0 && Math.abs(diferencia) <= 0.05) {
            const ultima = transformedPaymentLines[transformedPaymentLines.length - 1];
            ultima.Amount = addMoney(ultima.Amount, diferencia, decimalesDelPago);
            anotarAviso(
              "facturas",
              `${preAssignedDocNo}: el total de Business Central difiere en ${diferencia} del calculado acá; se ajustó la última forma de pago para que cubra ${totalSegunBC}`
            );
          } else if (Math.abs(diferencia) > 0.05) {
            // No se toca. Una diferencia así no es redondeo, y taparla dejaría
            // la venta registrada con un cobro que no corresponde a lo vendido.
            anotarAviso(
              "facturas",
              `${preAssignedDocNo}: Business Central calcula ${totalSegunBC} y acá se cobró ${sumaEnviada} (difieren en ${diferencia}). No se ajusta: revise el IVA o el precio del catálogo.`
            );
          }
        }
        const paymentLinesData = {
          data: JSON.stringify({ Sales_Payment_Lines: transformedPaymentLines }),
        };

        registro.log("Enviando líneas de pago");
        const responseLineasPago = await sendToBusinessCentral(
          "Importaciones_insertaPagosFacturasVenta",
          paymentLinesData,
          true
        );

        const rechazoDePagos = motivoDeRechazoBC(responseLineasPago);
        if (rechazoDePagos) {
          throw formatSyncError("PAYMENTS_SYNC_FAILED", rechazoDePagos, invoice);
        }

        syncStage = "payments";
        await db.invoices.update(invoice.id, {
          syncStage,
          syncError: null,
          syncErrorMessage: null,
          pendingSync: 0,
          lastSynced: getCurrentDateTimeISO(),
        });
        registro.log("Pagos enviados");
      }
    }

    // El estado local se actualiza para TODAS las líneas, también las deducidas.
    //
    // Va fuera del bloque de envío a propósito: si todas las líneas de un
    // documento fueran deducidas --una nota de crédito sobre una factura
    // histórica sin detalle de pago--, ese bloque no corre, y dejarlas dentro
    // las habría dejado con `pendingSync: 1` para siempre, reintentando un
    // envío que por definición nunca va a ocurrir.
    for (const line of paymentLines ?? []) {
      await db.invoicePaymentLines.update(line.id, {
        Document_No_: preAssignedDocNo,
        lastSynced: getCurrentDateTimeISO(),
        pendingSync: 0,
      });
    }

    // -----------------------------
    // 6. Contabilizar si el país lo hace desde el terminal
    // -----------------------------
    //
    // Costa Rica entra desde el 12/09/2026. Es el mismo mecanismo que Paraguay y
    // Honduras --la API estándar de Microsoft, `Microsoft.NAV.post`, con el
    // `System Id` que devolvió la cabecera-- y por eso se añade a la condición
    // en vez de escribir una rama nueva.
    //
    // Lo propio de Costa Rica es que el sellado va **dentro** del registro:
    // CertificaE se engancha a `Sales-Post`, así que cuando esta llamada vuelve
    // la clave de Hacienda ya existe. Medido: 2-4 segundos con el timbre
    // incluido. Quien espera a esa clave es `costarica/cierreFiscal.js`; acá no
    // se espera a nada, para que la sincronización de fondo siga costando lo
    // mismo en los tres países.
    //
    // Panamá entra el 17/09/2026, con la misma llamada. Lo suyo es al revés que lo
    // de Costa Rica: **no hay timbre que esperar**. La factura electrónica de
    // Panamá no se emitirá hasta que CertificaE la desarrolle, así que el documento
    // queda registrado y sin CUFE, y nadie espera nada. Decisión D4 de
    // `12 - Panamá/campos-excpan-tpv-2026-09-17.md`.
    // Quién contabiliza se lee de `integracionPorPais`, no de una lista aquí.
    //
    // Hasta el 17/10/2026 esto era una cadena de `||` con cinco países, y al lado
    // existía `registraDesdeElTerminal` en `COMPORTAMIENTO` **que no leía nadie**:
    // la bandera estaba escrita, comentada y con prueba, y aun así era decorativa.
    // Quien fuera a encender Puerto Rico ponía la bandera en `true` y no encendía
    // nada, porque la decisión se tomaba acá. Las dos fuentes ya no pueden
    // discrepar porque hay una sola.
    //
    // El cambio no mueve a nadie: los cuatro países cuya bandera ya decía `true`
    // --Paraguay, Honduras, Costa Rica y Panamá-- estaban en la lista, y a Perú,
    // que estaba en la lista con la bandera en `false` por tener su entrada
    // vacía, se le puso `true` en la tabla para que diga lo que de verdad hace.
    if (comportamientoDelPais(country.code, "registraDesdeElTerminal")) {
      if (!systemId) registro.warn("Factura sin systemId, no se contabiliza");
      else {
        // Antes de registrar, lo que Business Central tiene cobrado.
        //
        // Un pago reenviado podía perderse en silencio --la acción respondía `Ok`
        // sin cambiar el importe de una línea que ya existía--, y el registro se
        // rechazaba después con `PAGO_INCOMPLETO`. Ver `cobroAntesDeRegistrar.js`.
        // Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H1.
        const estadoAntesDeRegistrar = await consultarEstadoFactura({
          tienda: updatedInvoice.Tienda || updatedInvoice.tienda,
          caja: updatedInvoice.Caja || updatedInvoice.caja,
          idTpv: updatedInvoice.Id_TPV,
          documentType: mapDocumentTypeForBC(updatedInvoice.Document_Type),
        });
        const cobroQueNoCuadra = cobroQueNoCuadraEnBC(estadoAntesDeRegistrar, country);
        if (cobroQueNoCuadra) {
          const aviso = avisoDelCobroQueNoCuadra(cobroQueNoCuadra, country);

          // Los dos sentidos no se arreglan igual, así que no se tratan igual.
          //
          // Lo que **falta** se manda de nuevo: se vuelve a la etapa de líneas y
          // la corrida siguiente reenvía los pagos.
          //
          // Lo que **sobra** no se puede arreglar desde aquí --los pagos no se
          // retiran, acuerdo del 12/09--, así que la etapa no se toca: reenviar
          // no quita nada y volver atrás sólo repetiría el envío. Queda
          // pendiente hasta que alguien retire el pago en Business Central. Es
          // el mismo criterio que `dejarPendientePorLosPagos`.
          if (cobroQueNoCuadra.sobra) {
            return await dejarPendientePorElCobroQueSobra(invoice, aviso);
          }

          return await volverAMandarLosPagos(invoice, aviso);
        }

        // Y cuántos pagos tiene. El infrapago ya lo frenó lo de arriba; esto
        // frena el pago que se quitó en el terminal y sigue en Business Central,
        // que ninguna suma delata. Acuerdo
        // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1 · TPV.
        const pagosQueNoCuadran = pagosQueNoCuadranEnBC(estadoAntesDeRegistrar, paymentLines);
        if (pagosQueNoCuadran) {
          return await dejarPendientePorLosPagos(invoice, pagosQueNoCuadran);
        }

        try {
          registro.log("Contabilizando");
          const isCreditMemo = invoice.Document_Type === "Credit Memo";

          const intento = await registrarEnBusinessCentral(systemId, isCreditMemo, country);

          if (intento.ok) {
            //Chequeamos si BC dio error pero contabilizo igualmente
            return await recuperarFacturaContabilizada(invoice, country);
          }

          // Los pagos no cubren el documento: no se registró y no hay número que
          // recuperar. Hasta ahora caía en el camino de abajo y quedaba con
          // `pendingSync: 0`, fuera de la sincronización para siempre.
          if (esRechazoPorPagoIncompleto(intento.message)) {
            return await volverAMandarLosPagos(invoice, intento.message);
          }

          // La nota de crédito abona más de lo que queda de la factura: la
          // puerta 2 de Business Central, al registrar. Sale de la cola sin el
          // modal y sin buscar un número que no existe.
          if (esRechazoPorAbonoQueExcede(intento.message)) {
            return await dejarFueraPorAbonoQueExcede(invoice, intento.message);
          }

          // A la tienda le falta la cuenta de pago de esta forma de pago. El
          // `Error` de `CuentaPago` sale desde dentro del registro, así que no
          // se quedó el cobro sin contabilizar: se deshizo el registro entero y
          // el borrador se quedó en Business Central.
          //
          // Sin esto caía en el camino de abajo: modal con el texto crudo de AL
          // --«Falta la fila en Payment Method Det TPV»-- y el documento fuera
          // de la cola con `pendingSync: 0`, que es lo peor de las dos opciones,
          // porque esto sí se arregla solo en cuanto alguien configure la fila.
          if (esRechazoPorCuentaDePagoSinConfigurar(intento.message)) {
            return await dejarPendientePorLaCuentaDePago(invoice, intento.message);
          }

          // Con respuesta de Business Central se enseña su motivo, que es el
          // que el cajero puede corregir. Sin respuesta --la tienda se quedó
          // sin red-- se dice eso, y no «Network Error» bajo un título que
          // culpa a Business Central de algo de lo que ni se enteró.
          const aviso = avisoDelFallo({
            origen: intento.origen,
            mensaje: intento.message,
            queNoSePudo: "No se pudo contabilizar el documento en Business Central.",
          });
          window.messageApi?.error?.(aviso.texto, { modal: true, title: aviso.titulo });
          // El motivo viaja pegado al error. Sin esto se queda en el modal y no
          // llega al `catch`, que es donde se guarda la ficha del documento.
          throw Object.assign(new Error("No fue posible contabilizar la factura."), {
            motivoDeBC: aviso.texto,
          });
        } catch (err) {
          registro.warn("Error al contabilizar, pero la factura está sincronizada en BC", err);
          try {
            //Chequeamos si BC dio error pero contabilizo igualmente
            return await recuperarFacturaContabilizada(invoice, country);
          } catch (recoveryError) {
            registro.warn(
              "No se pudo recuperar consecutivo final tras error de contabilización",
              recoveryError
            );

            // La consideramos sincronizada, pero no contabilizada.
            //
            // **El motivo se guarda, no se borra.** Hasta ahora aquí se escribía
            // `syncError: null` y `syncErrorMessage: null`: lo que dijo Business
            // Central se enseñaba una vez en el modal y desaparecía. Después
            // nadie podía averiguar por qué ese documento se quedó sin
            // registrar, ni desde el terminal ni preguntándole a Datos --y del
            // otro lado sólo se ve un borrador varado, que no dice nada--.
            //
            // Se marca `syncError` porque es verdad que hubo un fallo. Que el
            // documento salga de la cola es otra decisión, y esa no cambia.
            const motivo =
              err?.motivoDeBC ||
              err?.message ||
              "No se pudo contabilizar el documento en Business Central.";

            await db.invoices.update(invoice.id, {
              posted: 0,
              syncStage: "payments",
              syncError: true,
              syncErrorMessage: motivo,
              pendingSync: 0,
              lastSynced: getCurrentDateTimeISO(),
            });

            return {
              success: true,
              message:
                "Factura sincronizada, pero no se pudo contabilizar automáticamente. Reintente desde BC.",
            };
          }
        }
      }
    }

    // -----------------------------
    // 7. Finalizado (los países que no registran desde el terminal)
    // -----------------------------
    //
    // `enviada`, no `posted`: en estos cuatro países el terminal **no
    // contabiliza**. El documento está entero en Business Central —cabecera,
    // líneas y pagos— y esperando a que alguien lo registre allá.
    //
    // Decía `posted`, que es el mismo nombre que se escribe cuando la factura sí
    // se contabilizó, así que el estado no distinguía «terminada» de «esperando
    // que la registren». El campo `posted` sí lo distingue —queda en 0— pero hay
    // que acordarse de mirarlo: quien leyera sólo el estado daba por registrado
    // un documento que no lo está. Para el listado las dos cuentan como
    // completas, que es lo correcto: del lado del terminal no queda nada por
    // hacer.
    await db.invoices.update(invoice.id, {
      pendingSync: 0,
      syncStage: "enviada",
      syncError: null,
      syncErrorMessage: null,
      lastSynced: getCurrentDateTimeISO(),
    });
    return { success: true, message: "Factura sincronizada correctamente" };
  } catch (error) {
    // `DATOS_INVALIDOS` no se reintenta: volver a mandar el mismo dato daría el
    // mismo rechazo. Deja de estar pendiente hasta que alguien lo corrija, y el
    // motivo queda escrito en la fila para que se sepa qué corregir.
    // `SIN_CORREO`, igual: el reintento no captura el correo. Y
    // `RECHAZO_DEFINITIVO`: BC contestaría lo mismo (`rechazoDefinitivo.js`).
    const noValeReintentar = [
      "NO_LINES",
      "CLIENT_NOT_SYNCED",
      "DATOS_INVALIDOS",
      "DATOS_INVALIDOS_DGI",
      "SIN_CORREO",
      "RECHAZO_DEFINITIVO",
    ];
    const shouldRemainPending = !noValeReintentar.includes(error?.code);

    await db.invoices.update(invoice.id, {
      pendingSync: shouldRemainPending ? 1 : 0,
      syncError: true,
      syncErrorMessage: error?.message || "Error desconocido",
      lastSynced: getCurrentDateTimeISO(),
    });

    if (error?.code) {
      return { ...error };
    }

    return { ...formatSyncError("UNKNOWN_ERROR", error.message, invoice) };
  }
};

export const syncInvoiceToBC = async (invoice, country) => {
  if (invoicesSyncingNow.has(invoice.id)) {
    return formatSyncError("ALREADY_SYNCING", "", invoice);
  }

  invoicesSyncingNow.add(invoice.id);

  try {
    return await syncInvoiceToBCInternal(invoice, country);
  } finally {
    invoicesSyncingNow.delete(invoice.id);
  }
};

// Sincronizar todas las facturas pendientes
export const syncInvoices = async (country) => {
  try {
    // Obtener facturas pendientes de sincronización que NO sean borradores
    const pendingInvoices = await db.invoices
      .where("pendingSync")
      .equals(1)
      .and((invoice) => !invoice.isDraft && !invoice.deleted) // Excluir borradores y facturas eliminadas
      .toArray();

    const results = [];
    const pendingClientSyncInvoices = [];

    // Sincronizar cada factura pendiente
    for (const invoice of pendingInvoices) {
      const result = await syncInvoiceToBC(invoice, country);

      if (result.pendingClientSync) {
        // La factura tiene un cliente que no está sincronizado
        pendingClientSyncInvoices.push({
          invoiceId: invoice.id,
          clientId: result.clientId,
          message: result.message,
        });
        registro.log(
          `Factura ${invoice.id} pendiente de sincronización del cliente ${result.clientId}`
        );
      } else {
        results.push(result);
      }
    }

    // Si hay facturas pendientes de sincronización de clientes, mostrar en consola
    if (pendingClientSyncInvoices.length > 0) {
      registro.log(
        "Las siguientes facturas están pendientes de sincronización de clientes:",
        pendingClientSyncInvoices
      );
    }

    return {
      success: true,
      results,
      pendingClientSync: pendingClientSyncInvoices.length > 0,
      pendingClientSyncInvoices,
    };
  } catch (error) {
    registro.error("Error al sincronizar facturas:", error);
    return {
      success: false,
      message: motivoParaElCajero(error, "No se pudieron sincronizar los documentos."),
    };
  }
};

export async function updateDocumentNumberReferences(oldNo, newNo, invoiceId) {
  // 1. Actualizar invoiceLines
  await db.invoiceLines.where("invoiceId").equals(invoiceId).modify({ invoiceNo: newNo });

  // 2. Actualizar invoicePaymentLines
  await db.invoicePaymentLines.where("invoiceId").equals(invoiceId).modify({ Document_No_: newNo });

  // 3. Actualizar formaDePago
  //
  // Dexie rechaza `equals(null)` / `equals(undefined)` con «Invalid key
  // provided», y eso tumbaba la validación previa a la nota de crédito en Perú
  // cuando la fila local no tenía número de borrador. Sin número viejo no hay
  // pagos que renombrar por él.
  if (oldNo == null || oldNo === "") return;
  await db.formaDePago.where("DocumentNo").equals(oldNo).modify({ DocumentNo: newNo });
}

export async function getFinalDocumentNo(invoice, country) {
  /**
   * Determinar si el documento ya tiene número definitivo.
   * Pre_Assigned_No vacío significa:
   *   → el campo "No" es el número pre-asignado
   *   → aún NO se ha recuperado el número definitivo
   *
   * Acá decía que «las Credit Memo siempre cuentan con Pre_Assigned_No de la
   * factura relacionada». No es cierto, y costó una investigación entera:
   * el número de borrador de un abono es SUYO, de la serie de abonos.
   *
   * Medido el 11/09/2026 sobre 228 abonos contabilizados --199 en Ecuador TEST
   * y 29 en Costa Rica producción--: todos con serie de abono (VNC/VN),
   * ninguno con el número de la factura que corrigen. El terminal tampoco lo
   * hereda: `InvoiceManagement` pone `Pre_Assigned_No: ""` al crear el abono, y
   * la sincronización escribe el que devuelve Business Central.
   */
  const isCreditMemo = invoice.Document_Type === "Credit Memo";
  const hasDefinitiveNumber = invoice.No;

  // Si ya tiene número definitivo, serie y systemId, devolvemos el existente y salimos.
  if (
    hasDefinitiveNumber &&
    (invoice.No_Serie_NCF_Facturas || invoice.No_Serie_NCF_Abonos) &&
    invoice.systemId
  ) {
    return invoice.No;
  }

  let cliente = invoice.Customer_No ?? invoice.Sell_to_Customer_No_;

  try {
    if (typeof cliente === "number" && !isNaN(cliente)) {
      const clientData = await getClientById(cliente);
      cliente = clientData?.No ?? null;
    }
  } catch (error) {
    cliente = null;
  }

  // El mismo criterio que `buildFilters`, y ahora literalmente el mismo código:
  // estaba escrito dos veces, con el comentario admitiéndolo.
  const campoCaja = campoDeLaCaja(country?.code, true);

  const filters = [
    cliente && `Sell_to_Customer_No eq '${cliente}'`,
    `Pre_Assigned_No eq '${invoice.Pre_Assigned_No}'`,
    `${campoCaja} eq '${invoice.caja}'`,
    `Posting_Date eq ${invoice.Posting_Date}`,
  ].filter(Boolean); // elimina vacíos

  const filter = filters.join(" and ");

  // Honduras y El Salvador leen la factura registrada por su propia API --allá
  // OData no funciona-- pero el abono registrado sigue por OData. Esa asimetría
  // vivía en un `isApi = !isCreditMemo` fácil de perder; ahora cada entidad
  // declara su camino por separado en `config/integracionPorPais.js`.
  const { endpoint, apiV2 } = entidadDelPais(
    country?.code,
    isCreditMemo ? "abonosRegistrados" : "facturasRegistradas"
  );

  const results = await fetchFromBusinessCentral(endpoint, filter, apiV2);

  if (!Array.isArray(results) || results.length === 0) {
    throw new Error(
      `No fue posible recuperar el número de factura con Pre_Assigned_No '${invoice.Pre_Assigned_No}'.`
    );
  }

  const finalDocumentNo = results[0].No;

  if (!finalDocumentNo) {
    return null;
  }

  // El borrador que devuelve Business Central manda sobre el local: es el mismo
  // por el que se filtró, y si la fila local no lo tenía, así queda completa.
  const preAssignedNo = results[0]?.Pre_Assigned_No || invoice.Pre_Assigned_No;

  await db.invoices.update(invoice.id, {
    Pre_Assigned_No: preAssignedNo,
    No: finalDocumentNo,
    No_Serie_NCF_Facturas: results[0]?.No_Serie_NCF_Facturas || "",
    No_Serie_NCF_Abonos: results[0]?.No_Serie_NCF_Abonos || "",
    No_Comprobante_Fiscal: results[0]?.No_Comprobante_Fiscal,
    // El consecutivo del comprobante electrónico de Costa Rica --lo que el
    // tiquete imprime como «Tiquete Electrónico», y el tramo largo de la clave
    // numérica de Hacienda--. La respuesta ya lo traía; simplemente no se
    // guardaba, así que al reimprimir un documento el campo salía vacío.
    //
    // Se copia sólo si viene: los demás países no publican esta clave, y
    // escribir `undefined` encima borraría lo que hubiera.
    ...(results[0]?.EXCCRIElectronicReferenceNo
      ? { EXCCRIElectronicReferenceNo: results[0].EXCCRIElectronicReferenceNo }
      : {}),
    // `EXCCRIFiscalReceiptNo` se guardaba aquí y **no lo leía nadie**. Retirado
    // el 12/09/2026, después de medir qué es: sobre las 27.336 facturas
    // contabilizadas de 2026 en Costa Rica producción viene informado en 19.611
    // --el 71,7%-- y **ninguna sale del terminal**. Es numeración de otro canal,
    // el de comercio electrónico.
    //
    // Y no es el consecutivo fiscal: son seis dígitos que repiten la cola del
    // propio número de documento --`VFR1-006509` lleva `006509`--, mientras que
    // el consecutivo de verdad, los veinte dígitos, está en
    // `EXCCRIElectronicReferenceNo`, que es el que el tiquete sí usa.
    //
    // Un campo que se escribe y nadie lee es una trampa para quien lo encuentre
    // luego y piense que significa algo.
    Posting_Date: results[0]?.Posting_Date,
    systemId: results[0]?.systemId || results[0]?.SystemId || "",
    posted: 1,
    syncStage: "posted",
    pendingSync: 0,
    lastSynced: getCurrentDateTimeISO(),
  });

  await updateDocumentNumberReferences(preAssignedNo, finalDocumentNo, invoice.id);

  return finalDocumentNo;
}

export async function recuperarFacturaContabilizada(invoice, country) {
  const latestInvoice = await db.invoices.get(invoice.id);
  //Si tiene NoFinal actualiza estado invoice dentro de getFinalDocumentNo
  const finalNo = await getFinalDocumentNo(latestInvoice, country);

  // Si no recupera NoFinal significa que no contabilizó: se guarda en el paso
  // anterior, que es `payments` -- en plural.
  //
  // Decía `payment`, y ese nombre no lo reconoce nadie: el listado da por
  // sincronizada una factura cuyo estado sea `payments` o `posted`, así que una
  // factura completa en Business Central, con sus líneas y sus pagos, se
  // mostraba como «Sin sincronizar». Una letra.
  if (!finalNo) {
    await db.invoices.update(invoice.id, {
      posted: 0,
      syncStage: "payments",
      pendingSync: 0,
      lastSynced: getCurrentDateTimeISO(),
    });
  }

  return {
    success: true,
    message: finalNo
      ? "Factura contabilizada correctamente."
      : "Factura sincronizada, pero no se pudo contabilizar automáticamente. Reintente desde BC.",
  };
}

const normalizeDateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const isCorrelativeCandidate = (invoice, documentType, countryCode) => {
  if (invoice.deleted || invoice.isDraft) return false;
  // En Perú una boleta cuenta como factura para este chequeo -- `matchesDocumentType`
  // ya lo resuelve, así que el listado de "Facturas de hoy" también revisa sus boletas.
  if (documentType && !matchesDocumentType(invoice.Document_Type, documentType, countryCode)) {
    return false;
  }
  if (
    !documentType &&
    invoice.Document_Type !== "Invoice" &&
    invoice.Document_Type !== "Credit Memo" &&
    invoice.Document_Type !== "Boleta"
  ) {
    return false;
  }
  if (!invoice.Pre_Assigned_No) return false;
  if (invoice.No) return false;
  return true;
};

/**
 * Los que ya tienen número pero de los que no se sabe si timbraron.
 *
 * `isCorrelativeCandidate` los descarta --sale por `if (invoice.No) return
 * false`--, y con razón: su correlativo ya llegó. Pero el **timbrado** llega
 * después y por otro lado, así que un documento puede tener su número definitivo
 * y seguir marcado «Sin timbrar» para siempre: nadie vuelve a preguntar.
 *
 * Sólo Costa Rica: es el único país con clave de Hacienda que traer.
 */
const leFaltaElTimbrado = (invoice, countryCode) => {
  if (!comportamientoDelPais(countryCode, "tieneTimbrado")) return false;
  if (invoice.deleted || invoice.isDraft) return false;
  if (!invoice.No) return false;
  // Con clave ya está resuelto, y la clave no cambia una vez asignada.
  return !String(invoice.Clave ?? "").trim();
};

/**
 * Cuántos documentos se consultan como mucho de una vez.
 *
 * Cada uno es una llamada a Business Central, y esto corre con el cajero
 * mirando. Un rango ancho traería cientos: mejor refrescar los más recientes y
 * que el resto se resuelva al reimprimirlos --el ticket pide el sello solo-- que
 * dejar la caja parada varios minutos.
 */
export const MAXIMO_TIMBRADOS_POR_TANDA = 120;

/**
 * Vuelve a preguntar por el timbrado de los que se quedaron sin él.
 *
 * Hace falta en dos sitios y por el mismo motivo: **el sello llega después del
 * documento y por otro lado**. Al actualizar correlativos, porque el correlativo
 * llega antes que el sello; y al traer históricos, porque el pull no trae nada
 * del timbrado --`Clave`, `Electronic_Document_Status` y `Mensaje` no están en
 * el mapeo-- y además reemplaza las filas del alcance, así que se lleva por
 * delante lo que ya se supiera.
 *
 * No lanza: que no se pueda refrescar el sello no puede tumbar la operación que
 * el cajero pidió, que era otra.
 *
 * @param {Array} documentos
 * @param {string} countryCode
 * @returns {Promise<{revisados: number, timbrados: number, sinRevisar: number}>}
 */
export const refrescarElTimbradoDe = async (documentos, countryCode) => {
  const pendientes = (documentos || []).filter((d) => leFaltaElTimbrado(d, countryCode));
  // Los más recientes primero: son los que el cajero tiene delante.
  const porFecha = [...pendientes].sort((a, b) =>
    String(b?.Posting_Date ?? "").localeCompare(String(a?.Posting_Date ?? ""))
  );
  const tanda = porFecha.slice(0, MAXIMO_TIMBRADOS_POR_TANDA);
  let timbrados = 0;

  for (const documento of tanda) {
    try {
      const timbrado = await traerElTimbrado(documento);
      if (timbrado?.clave) timbrados += 1;
    } catch (error) {
      registro.warn(`No se pudo refrescar el timbrado de ${documento.No}: ${error.message}`);
    }
  }

  return {
    revisados: tanda.length,
    timbrados,
    sinRevisar: pendientes.length - tanda.length,
  };
};

const processCorrelativeCandidates = async (country, candidates, todos = null) => {
  const result = {
    totalCandidates: candidates.length,
    updated: 0,
    notFound: 0,
    errors: [],
  };

  for (const invoice of candidates) {
    try {
      const finalNo = await getFinalDocumentNo(invoice, country);
      if (finalNo) {
        result.updated += 1;
      } else {
        result.notFound += 1;
      }
    } catch (error) {
      result.notFound += 1;
      result.errors.push({
        id: invoice.id,
        preAssignedNo: invoice.Pre_Assigned_No,
        type: invoice.Document_Type,
        message: error?.message || "Error desconocido",
      });
    }
  }

  // Segunda pasada: el timbrado de los que ya tenían número.
  //
  // Se hace sobre TODO el alcance y no sólo sobre los candidatos, porque los que
  // interesan son justamente los que el primer filtro descarta: los que ya
  // recibieron su correlativo y se quedaron con el sello sin preguntar.
  //
  // Se releen de la base para que los que acaban de recibir su número en la
  // vuelta de arriba entren también en ésta.
  const delAlcance = todos || candidates;
  const frescos = await Promise.all(delAlcance.map((d) => db.invoices.get(d.id).catch(() => null)));
  const timbrado = await refrescarElTimbradoDe(frescos.filter(Boolean), country?.code);
  result.timbradosRevisados = timbrado.revisados;
  result.timbrados = timbrado.timbrados;

  return result;
};

/** Actualiza correlativos de un subconjunto de facturas */
export const refreshFinalDocumentNumbersForList = async (
  country,
  invoices,
  { documentType = "Invoice" } = {}
) => {
  const candidates = invoices.filter((invoice) =>
    isCorrelativeCandidate(invoice, documentType, country?.code)
  );
  return processCorrelativeCandidates(country, candidates, invoices);
};

export const refreshFinalDocumentNumbers = async (country, filters = {}) => {
  const { Caja, Turno, FechaInicio, FechaFin } = filters;

  const startDate = normalizeDateOnly(FechaInicio);
  const endDate = normalizeDateOnly(FechaFin);

  // El alcance entero, antes de quedarse con los candidatos a correlativo: la
  // segunda pasada --la del timbrado-- necesita justo los que este filtro
  // descarta por tener ya su número.
  const delAlcance = await db.invoices
    .filter((invoice) => {
      if (invoice.deleted || invoice.isDraft) return false;
      if (Caja && invoice.caja !== Caja) return false;

      const postingDate = normalizeDateOnly(invoice.Posting_Date);
      if (startDate && postingDate != null && postingDate < startDate) return false;
      if (endDate && postingDate != null && postingDate > endDate) return false;
      if ((startDate || endDate) && postingDate == null) return false;

      return !Turno || invoice.turno === Turno;
    })
    .toArray();

  const candidates = await db.invoices
    .filter((invoice) => {
      if (!isCorrelativeCandidate(invoice)) return false;

      if (Caja && invoice.caja !== Caja) return false;
      if (Turno && invoice.turno !== Turno) return false;

      const postingDate = normalizeDateOnly(invoice.Posting_Date);
      if (startDate && postingDate != null && postingDate < startDate) return false;
      if (endDate && postingDate != null && postingDate > endDate) return false;
      if ((startDate || endDate) && postingDate == null) return false;

      return true;
    })
    .toArray();

  return processCorrelativeCandidates(country, candidates, delAlcance);
};
